import { API, doPost2ErrorHandling } from './common'
import { removeEmptyObjects } from 'util/removeEmptyObjects'
import { registerGuest } from './auth'

/*
 * Form-step submission services.
 *
 * FORM break steps submit through the dedicated /form-step + /token endpoints,
 * separate from the legacy lead-gen flow in services/leadgen.js. Flow:
 *   1. generateFormStepToken   -> POST /token/generate-token  (Turnstile-gated; emails the 6-digit code)
 *   2. user enters the code
 *   3. verifyAndSubmitFormStep -> POST /form-step/verify-and-submit (server validates + consumes the code)
 */

// POST /token/generate-token — issues the email verification code for a form step.
// Turnstile is validated here (not at submit). tokenSource defaults to FORM.
export const generateFormStepToken = async ({
  email,
  firstName,
  lastName,
  tokenSource = 'FORM',
  tokenSourceId,
  permalink,
  turnstileToken,
  type = 'EMAIL',
} = {}) => {
  const payload = removeEmptyObjects({
    email,
    firstName: firstName || email,
    lastName: lastName || '',
    tokenSource,
    tokenSourceId,
    permalink,
    turnstileToken,
    type: type.toUpperCase(),
  })

  return doPost2ErrorHandling(`/token/generate-token`, payload)
}

// POST /form-step/verify-and-submit — the 6-digit email code is validated and
// consumed server-side, so we submit it directly: no /token/validate-token
// pre-call (that would consume the single-use code). Turnstile normally only
// issues the code at /token/generate-token; a step with skipEmailVerification
// (BR-5706) sends no code, and the API checks the Turnstile token instead.
export const submitFormStep = ({
  breakId,
  stepId,
  values,
  formStepEntry,
  inventorySelections,
  email,
  firstName,
  lastName,
  phoneNumber,
  token,
  tokenSource,
  turnstileToken,
  turnstileTokenType,
} = {}) => {
  const payload = {
    breakId,
    stepId,
    values,
    email,
    firstName,
    lastName,
    phoneNumber,
    token,
    tokenSource: tokenSource || 'FORM',
    turnstileToken,
    turnstileTokenType,
    // formStepEntry carries the guest slots (additionalSlots), cascading/inventory
    // selections and custom field values. Dropping it here silently loses the guest
    // data and pins slot_count to 1, so forward it whenever the step produced one.
    ...(formStepEntry ? { formStepEntry } : {}),
    ...(Array.isArray(inventorySelections) && inventorySelections.length > 0
      ? { inventorySelections }
      : {}),
  }

  return doPost2ErrorHandling(`/form-step/verify-and-submit`, payload)
}

// Handler the UserVerification modal calls with the merged confirmation payload
// (including the 6-digit code as `token`). The code is verified server-side as
// part of the submit, so this is a single call; a bad/expired code makes the
// endpoint throw, which the modal surfaces via onValidationError.
//
// /form-step/verify-and-submit is auth-required. The step-start gate normally
// registers a guest (or the user is logged in) before a form step is interactive,
// but a step with no start screen — or a failed background guest registration —
// could otherwise reach here token-less and 401. Self-heal by ensuring a session
// token before submitting.
export const verifyAndSubmitFormStep = async (confirmationData = {}) => {
  if (!API.tokenPresent()) {
    try {
      await registerGuest()
    } catch (e) {
      // Leave the submit to surface a clear error if we're still unauthenticated.
      console.warn('form-step: guest registration before submit failed', e)
    }
  }

  return submitFormStep(confirmationData)
}
